import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  generatePagePlan,
  addOpportunityToPlan,
  ignoreOpportunity,
  generateBlogFromClusterBlueprint,
  generateClusterBlueprints,
  generateTopicBlueprint,
  ignoreCluster,
  ignoreImprovement
} from 'src/common/Scraper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import {
  posthogService,
  PostHogEvent
} from 'src/common/services/posthogService';
import { setOrbitAISnackBar } from 'src/redux/siteAuditReducer';

export function useContentStrategyActions() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const siteAuditDetails = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  );
  const siteId = siteAuditDetails?._id;
  const userId = siteAuditDetails?.user_id;

  const generatePagePlanMutation = useMutation({
    mutationFn: ({
      opportunityId,
      regenerate,
      customEndpoint
    }: {
      opportunityId: string;
      regenerate?: boolean;
      customEndpoint?: string;
    }) => generatePagePlan(opportunityId, siteId, regenerate, customEndpoint),
    onSuccess: (data, variables) => {
      // Track Orbit AI Page Plan generation in PostHog
      // Records site ID, user ID, opportunity ID, and whether it was a regeneration
      posthogService.track(PostHogEvent.ORBIT_AI_PAGE_PLAN_GENERATED, {
        site_id: siteId,
        user_id: userId,
        opportunity_id: variables?.opportunityId,
        regenerate: Boolean(variables?.regenerate)
      });
      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
    },
    onError: (err: any) => {
      dispatch(
        setOrbitAISnackBar({
          status: 'error',
          message:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            err?.error ||
            'Something went wrong while generating the page plan. Please try again.'
        })
      );
    }
  });

  const addToPlanMutation = useMutation({
    mutationFn: ({ opportunityId }: { opportunityId: string }) =>
      addOpportunityToPlan(opportunityId, siteId),
    onSuccess: (data, variables) => {
      // Track Orbit AI Opportunity added to plan in PostHog
      posthogService.track(PostHogEvent.ORBIT_AI_OPPORTUNITY_ADDED, {
        site_id: siteId,
        user_id: userId,
        opportunity_id: variables?.opportunityId
      });

      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
    }
  });

  const ignoreOpportunityMutation = useMutation({
    mutationFn: ({
      opportunityId,
      customEndpoint
    }: {
      opportunityId: string;
      customEndpoint?: string;
    }) => ignoreOpportunity(opportunityId, siteId, customEndpoint),
    onSuccess: (data, variables) => {
      // Track Orbit AI Opportunity ignored / dismissed in PostHog
      posthogService.track(PostHogEvent.ORBIT_AI_OPPORTUNITY_IGNORED, {
        site_id: siteId,
        user_id: userId,
        opportunity_id: variables?.opportunityId
      });
      dispatch(
        setOrbitAISnackBar({
          status: 'success',
          message: 'Opportunity ignored successfully'
        })
      );
      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
    },
    onError: (err: any) => {
      dispatch(
        setOrbitAISnackBar({
          status: 'error',
          message:
            err?.message ||
            err?.error ||
            'Something went wrong while ignoring the opportunity. Please try again.'
        })
      );
    }
  });

  const generateBlogFromBlueprintMutation = useMutation({
    mutationFn: ({
      blueprintId,
      suggestion,
      customEndpoint
    }: {
      blueprintId: string;
      suggestion?: string;
      customEndpoint?: string;
    }) =>
      generateBlogFromClusterBlueprint(blueprintId, suggestion, customEndpoint),
    onSuccess: (data, variables) => {
      // Track Orbit AI Blog generated from cluster blueprint in PostHog
      posthogService.track(PostHogEvent.ORBIT_AI_BLOG_GENERATED, {
        site_id: siteId,
        user_id: userId,
        blueprint_id: variables?.blueprintId,
        has_suggestion: Boolean(variables?.suggestion)
      });

      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
      queryClient.invalidateQueries({
        queryKey: ['blog-blueprints']
      });
      queryClient.invalidateQueries({
        queryKey: ['get-generated-blog-by-blueprint']
      });
    },
    onError: (err: any) => {
      dispatch(
        setOrbitAISnackBar({
          status: 'error',
          message:
            err?.message ||
            err?.error ||
            'Something went wrong while generating the blog post. Please try again.'
        })
      );
    }
  });

  const generateClusterBlueprintsMutation = useMutation({
    mutationFn: ({
      clusterId,
      customEndpoint
    }: {
      clusterId: string;
      customEndpoint?: string;
    }) => generateClusterBlueprints(clusterId, siteId, customEndpoint),
    onSuccess: (data, variables) => {
      // Track Orbit AI Cluster Blueprints generated in PostHog
      posthogService.track(PostHogEvent.ORBIT_AI_CLUSTER_BLUEPRINTS_GENERATED, {
        site_id: siteId,
        user_id: userId,
        cluster_id: variables?.clusterId
      });

      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
    }
  });

  const generateTopicBlueprintMutation = useMutation({
    mutationFn: ({
      clusterId,
      topicIndex,
      customEndpoint
    }: {
      clusterId: string;
      topicIndex: number;
      customEndpoint?: string;
    }) => generateTopicBlueprint(clusterId, topicIndex, siteId, customEndpoint),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
    }
  });

  const ignoreClusterMutation = useMutation({
    mutationFn: ({
      clusterId,
      customEndpoint
    }: {
      clusterId: string;
      customEndpoint?: string;
    }) => ignoreCluster(clusterId, siteId, customEndpoint),
    onSuccess: () => {
      dispatch(
        setOrbitAISnackBar({
          status: 'success',
          message: 'Cluster ignored successfully'
        })
      );
      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
    },
    onError: (err: any) => {
      dispatch(
        setOrbitAISnackBar({
          status: 'error',
          message:
            err?.message ||
            err?.error ||
            'Something went wrong while ignoring the cluster. Please try again.'
        })
      );
    }
  });

  const ignoreImprovementMutation = useMutation({
    mutationFn: ({
      improvementId,
      customEndpoint
    }: {
      improvementId: string;
      customEndpoint?: string;
    }) => ignoreImprovement(improvementId, siteId, customEndpoint),
    onSuccess: () => {
      dispatch(
        setOrbitAISnackBar({
          status: 'success',
          message: 'Improvement ignored successfully'
        })
      );
      queryClient.invalidateQueries({
        queryKey: ['content-strategy', userId, siteId]
      });
    },
    onError: (err: any) => {
      dispatch(
        setOrbitAISnackBar({
          status: 'error',
          message:
            err?.message ||
            err?.error ||
            'Something went wrong while ignoring the improvement. Please try again.'
        })
      );
    }
  });

  return {
    generatePagePlanMutation,
    addToPlanMutation,
    ignoreOpportunityMutation,
    generateBlogFromBlueprintMutation,
    generateClusterBlueprintsMutation,
    generateTopicBlueprintMutation,
    ignoreClusterMutation,
    ignoreImprovementMutation
  };
}
